import * as React from 'react';

import { Button, cn, Row } from '@rematter/ui-web';

import { useTranslations } from '../../../i18n/useTranslations';

import './HueColorPicker.css';

const HUE_GRADIENT =
  'linear-gradient(to right, hsl(0,75%,50%), hsl(60,75%,50%), hsl(120,75%,50%), hsl(180,75%,50%), hsl(240,75%,50%), hsl(300,75%,50%), hsl(360,75%,50%))';

type HueColorPickerProps = {
  value: number | null | undefined;
  onChange: (hue: number | null) => void;
};

//
// ⚛️ ⚛️ ⚛️ HUE COLOR PICKER ⚛️ ⚛️ ⚛️
//
export const HueColorPicker = ({ value, onChange }: HueColorPickerProps) => {
  const t = useTranslations();

  const isAuto = value == null;
  const hue = value ?? 180;

  const handleSliderChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newHue = Number(e.target.value);
      onChange(newHue === 0 ? 360 : newHue);
    },
    [onChange],
  );

  return (
    <div className="@HueColorPicker flex flex-col gap-2">
      <Row yCenter gap2>
        <Button
          variant="outline"
          size="small"
          className={cn(
            'shrink-0 text-xs',
            isAuto && 'border-foreground ring-2 ring-foreground/20',
          )}
          onPress={() => onChange(null)}
        >
          {t.scaleTicketPro.settings.colorAuto}
        </Button>

        <div className="relative flex flex-1 items-center">
          <div
            className={cn(
              'absolute inset-0 rounded-md border border-neutral/20',
              isAuto && 'opacity-40',
            )}
            style={{ background: HUE_GRADIENT }}
          />
          <input
            type="range"
            min={1}
            max={360}
            value={hue}
            onChange={handleSliderChange}
            aria-label="Color hue"
            className="hue-slider relative z-10 h-6 w-full cursor-pointer touch-none appearance-none bg-transparent"
            style={
              {
                '--thumb-color': `hsl(${hue}, 75%, 50%)`,
              } as React.CSSProperties
            }
          />
        </div>

        {!isAuto && (
          <div
            className="size-6 shrink-0 rounded-full border border-neutral/20 shadow-sm"
            style={{ backgroundColor: `hsl(${value}, 75%, 50%)` }}
          />
        )}
      </Row>
    </div>
  );
};

export const hueToColor = (hue: number | null | undefined): string | null => {
  if (hue == null) return null;
  return `hsl(${hue}, 75%, 50%)`;
};
